import React, { useCallback } from 'react'
import styled from 'styled-components/native'
import { useNavigation } from '@react-navigation/native'
import I18n from 'APP/I18n'
import { CreditCardDetails } from 'APP/Store/Patient/types'
import Typography from 'APP/Converse/Typography'
import IconCard from 'APP/Converse/IconCard'
import List from 'APP/Components/List'
import CardholderNotice from 'APP/Components/CardholderNotice'
import { Images } from 'APP/Themes'

const STATIC_ICONS = {
  rightChevron: { src: 'chevron-right', variant: 'material' },
  creditCard: { src: 'credit-card', variant: 'material' },
  add: { src: 'plus', variant: 'material' },
} as const

interface PaymentCardProps {
  creditCard: CreditCardDetails
  onPress: () => void
}

const PaymentCard = React.memo<PaymentCardProps>(({ creditCard, onPress }) => {
  const PaymentIcon =
    Images.paymentMethods[creditCard.brand.toLowerCase() as keyof typeof Images.paymentMethods]

  const leftIcon = PaymentIcon
    ? {
        src: PaymentIcon,
        variant: 'image' as const,
        size: 'medium' as const,
        color: 'transparent' as const,
      }
    : STATIC_ICONS.creditCard

  const title = `${creditCard.brand} **** **** **** ${creditCard.last4Digits}`
  const description = `${I18n.t('Checkout.PaymentMethods.expires')} ${creditCard.expirationMonth}/${
    creditCard.expirationYear
  }`

  return (
    <IconCard
      testID={`SIMPLE_PAYMENT_OPTION_${creditCard.last4Digits}`}
      title={title}
      description={description}
      // @ts-expect-error color cannot be transparent
      leftIcon={leftIcon}
      rightIcon={STATIC_ICONS.rightChevron}
      onPress={onPress}
    />
  )
})

const PaymentMethodsContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

export const PaymentMethods = ({ methods }: { methods: CreditCardDetails[] }) => {
  const navigation = useNavigation()

  const handleNavigateToViewPayment = useCallback(
    () => navigation.navigate('viewPayment'),
    [navigation]
  )

  const handleNavigateToAddPayment = useCallback(
    () => navigation.navigate('addPayment'),
    [navigation]
  )

  return (
    <PaymentMethodsContainer testID="PAYMENT_METHODS_CONTAINER">
      <Typography variant="bodyLargeBold">{I18n.t('Checkout.PaymentMethods.title')}:</Typography>

      <CardholderNotice />

      <List>
        {methods.map((cc) => (
          <PaymentCard key={cc.last4Digits} creditCard={cc} onPress={handleNavigateToViewPayment} />
        ))}

        {methods.length === 0 && (
          <IconCard
            testID="ADD_NEW_PAYMENT_OPTION"
            title={I18n.t('Checkout.PaymentMethods.addCreditCard')}
            leftIcon={STATIC_ICONS.creditCard}
            rightIcon={STATIC_ICONS.add}
            onPress={handleNavigateToAddPayment}
          />
        )}
      </List>
    </PaymentMethodsContainer>
  )
}

export default PaymentMethods
